import {
  CreditCard,
  Flame,
  UtensilsCrossed,
  type LucideIcon,
} from "lucide-react";

/** Destaques estáticos da versão anterior — usados se a API estiver fora do ar. */
export interface Highlight {
  id: number;
  name: string;
  category: string;
  image_url: string;
}

export const FALLBACK_HIGHLIGHTS: Highlight[] = [
  {
    id: 1,
    name: "Ninja Smash Duplo",
    category: "Hambúrgueres",
    image_url:
      "https://images.unsplash.com/photo-1594212699903-ec8a3eca50f5?q=80&w=500&auto=format&fit=crop",
  },
  {
    id: 2,
    name: "Coca-Cola Gelada",
    category: "Bebidas",
    image_url:
      "https://images.unsplash.com/photo-1622483767028-3f66f32aef97?q=80&w=500&auto=format&fit=crop",
  },
  {
    id: 3,
    name: "Batata Frita Ninja",
    category: "Acompanhamentos",
    image_url:
      "https://images.pexels.com/photos/1893556/pexels-photo-1893556.jpeg?auto=compress&cs=tinysrgb&w=500",
  },
  {
    id: 4,
    name: "Combo Shogun",
    category: "Combo",
    image_url:
      "https://images.unsplash.com/photo-1610614819513-58e34989848b?q=80&w=500&auto=format&fit=crop",
  },
];

export interface Step {
  id: number;
  icon: LucideIcon;
  titulo: string;
  desc: string;
}

export const STEPS: Step[] = [
  {
    id: 1,
    icon: UtensilsCrossed,
    titulo: "Vá ao Cardápio",
    desc: "Navegue pelas categorias e selecione seus burgers clicando no ícone de adicionar.",
  },
  {
    id: 2,
    icon: CreditCard,
    titulo: "Finalize Online",
    desc: "Confira seu carrinho, preencha os dados de entrega.",
  },
  {
    id: 3,
    icon: Flame,
    titulo: "Aguarde a Entrega",
    desc: "Seu pedido entra em produção na hora! Prepare a mesa que estamos chegando.",
  },
];

export interface Slide {
  img: string;
  title: string;
  text: string;
}

export const ABOUT_SLIDES: Slide[] = [
  {
    img: "https://images.unsplash.com/photo-1550547660-d9450f859349",
    title: "Nossa História",
    text: "A Ninja Burger nasceu da ideia de que a rapidez de um mestre das sombras deve caminhar lado a lado com a precisão de um grande chef. Em nossa cozinha, cada ingrediente é selecionado com o rigor de um treinamento milenar, garantindo que o blend de carne suculento e os molhos artesanais cheguem à mesa com o frescor e a potência que o seu paladar exige. Mais do que apenas saciar a fome, nossa missão é oferecer uma experiência furtiva contra o comum, onde a tecnologia e o sabor se unem para criar o hambúrguer perfeito para quem não tem tempo a perder, mas não abre mão da excelência.",
  },
  {
    img: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd",
    title: "Ingredientes Frescos",
    text: "Na Ninja Burger, a excelência começa na seleção rigorosa de cada componente. Nosso blend de carne é preparado diariamente com cortes selecionados para garantir a suculência máxima, abraçado por pães artesanais de fermentação natural, como o brioche amanteigado, sempre selados na chapa. Utilizamos queijos de alta pureza para o derretimento perfeito e vegetais frescos, colhidos e fatiados no dia para manter a crocância. Para finalizar, nossos molhos autorais trazem o equilíbrio de sabores que transforma um simples lanche em uma experiência gastronômica ágil e memorável.",
  },
  {
    img: "https://images.unsplash.com/photo-1606755962773-d324e0a13086",
    title: "Receitas Exclusivas",
    text: "Os Lanches Especiais da Ninja Burger são o ponto alto de nossa engenharia gastronômica, onde a tradição do churrasco encontra a precisão técnica. Destacamos criações como o Shogun Double, com dois blends de carne de 160g e camadas generosas de bacon crocante caramelizado, e o Cyber Onion, que utiliza anéis de cebola empanados em panko para uma textura futurista. Cada especialidade é montada com queijos fundidos artesanalmente e finalizada com nossos molhos secretos, garantindo uma explosão de sabores que você não encontrará em nenhum outro lugar, tudo servido com a agilidade que o seu dia a dia exige.",
  },
  {
    img: "https://images.unsplash.com/photo-1550317138-10000687a72b",
    title: "Experiência",
    text: "A experiência na Ninja Burger foi projetada para ser tão ágil quanto impactante, unindo a velocidade da tecnologia à hospitalidade de uma hamburgueria de elite. Desde o momento em que o pedido é realizado em nossa interface intuitiva até a entrega final, cada etapa é cronometrada para que o cliente receba um produto em sua temperatura e textura ideais, sem esperas desnecessárias. Nosso ambiente — físico ou digital — reflete essa modernidade, oferecendo um acompanhamento em tempo real e um sistema de fidelidade que valoriza cada escolha, transformando o simples ato de comer um hambúrguer em um momento de conveniência, precisão e, acima de tudo, muito sabor.",
  },
];

export type GallerySize = "grande" | "normal" | "vertical" | "horizontal";

export interface GalleryImage {
  url: string;
  size: GallerySize;
}

export const GALLERY_IMAGES: GalleryImage[] = [
  { url: "https://images.unsplash.com/photo-1550547660-d9450f859349", size: "grande" },
  { url: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd", size: "normal" },
  { url: "https://images.unsplash.com/photo-1606755962773-d324e0a13086", size: "vertical" },
  { url: "https://images.unsplash.com/photo-1550317138-10000687a72b", size: "normal" },
  { url: "https://images.unsplash.com/photo-1600891964092-4316c288032e", size: "normal" },
  { url: "https://images.unsplash.com/photo-1551782450-a2132b4ba21d", size: "horizontal" },

  { url: "https://images.unsplash.com/photo-1513104890138-7c749659a591", size: "normal" },
  { url: "https://images.unsplash.com/photo-1551782450-17144efb9c50", size: "normal" },
  { url: "https://images.unsplash.com/photo-1544025162-d76694265947", size: "vertical" },
  { url: "https://images.unsplash.com/photo-1504674900247-0877df9cc836", size: "normal" },
  { url: "https://images.unsplash.com/photo-1559847844-5315695dadae", size: "horizontal" },
  { url: "https://images.unsplash.com/photo-1600891964599-f61ba0e24092", size: "normal" },
];
